import { useState } from 'react';
import { useHistory } from '@shared/core';
import { resolveCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';

/**
 * Slug of the section the guest landed on, pinned to the initial URL.
 *
 * Stacked layouts (Aloha, Brannan single-page) rewrite the URL as the guest scrolls
 * (`usePageInScreenWhenScrolling`), so reading the live slug would move any
 * target-dependent styling from section to section mid-scroll — itself a source of
 * layout shift. Undefined on root routes, where the landing section is the first one.
 */
export const useDeepLinkTargetSlug = (eventHandle: string): string | undefined => {
  // Read once off `history` rather than through `useCurrentPageSlug`: the value is pinned to the
  // landing URL, so subscribing to later slug changes only re-rendered the layout for a value the
  // hook then discarded.
  const history = useHistory();
  const [targetSlug] = useState(() => resolveCurrentPageSlug(history.location.pathname, eventHandle).currentPageSlug);
  return targetSlug;
};

/**
 * Match a page against the pinned deep-link target.
 *
 * Canonicalized, not a plain string compare: a page's `pageSlug` doesn't always equal the
 * slug in its URL. Migrated registrylinks pages carry `pageSlug: 'registry'` while the URL
 * (and canonical slug) is `registrylinks`, so a direct comparison silently missed the
 * target — the section then painted short and its growth displaced everything below it.
 * PRNT-4799.
 */
const canonicalize = (slug: string | undefined): string | undefined => (slug ? (toCanonicalMigratedSlug(slug) ?? slug.toLocaleLowerCase()) : undefined);

export const isDeepLinkTargetSlug = (pageSlug: string, targetSlug: string | undefined): boolean => {
  const target = canonicalize(targetSlug);
  return !!target && canonicalize(pageSlug) === target;
};
